import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import { AppDispatch, RootState } from 'store/store';
import { deleteRequest, getRequest, postRequest } from 'api/apiClient';
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { Location } from 'types/locationTypes';
import { hasPermission } from 'utils/utils';
import { TABLE_HEIGHT } from 'constants/ui';
import { LOCATION_DELETED_ID } from 'constants/userActivities';
import { describeLocation, describeLocationWorkflow, fetchLocations } from 'store/slices/locationsSlice';
import { Kiosk } from 'types/kioskTypes';
import { useTranslation } from 'react-i18next';

interface LocationsTableProps {
    handleEditClick: (location: Location) => void;
    handleAddClick: () => void;
}

const LocationsTable: React.FC<LocationsTableProps> = ({ handleEditClick, handleAddClick }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const { locations, fetchLocationsLoading } = useSelector((state: RootState) => state.locations);
    const selectedUserSites = useSelector((state: RootState) => state.users.selectedUserSites);


    const hasLocationWrite = hasPermission(tokenScopeList, orgId, 'o', 'location:w');
    const { t } = useTranslation();

    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'name',
            label: t('location.table.name'),
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            dataIndex: 'description',
            label: t('location.table.description'),
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
        },
        {
            dataIndex: 'address',
            label: t('location.table.address'),
            hidden: true,
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.address || '').localeCompare(b.address || ''),
        },
        {
            dataIndex: 'address2',
            label: t('location.table.address2'),
            hidden: true,
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.address2 || '').localeCompare(b.address2 || ''),
        },
        {
            dataIndex: 'city',
            label: t('location.table.city'),
            hidden: true,
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.city || '').localeCompare(b.city || ''),
        },
        {
            dataIndex: 'region',
            label: t('location.table.state'),
            hidden: true,
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.region || '').localeCompare(b.region || ''),
        },
        {
            dataIndex: 'postalcode',
            label: t('location.table.postalcode'),
            hidden: true,
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.postalcode || '').localeCompare(b.postalcode || ''),
        },
        {
            dataIndex: 'country',
            label: t('location.table.country'),
            hidden: true,
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.country || '').localeCompare(b.country || ''),
        },
        {
            dataIndex: 'phone',
            label: t('location.table.phone'),
            hidden: true,
            filter: {
                type: 'input',
            },
            sorter: (a, b) => (a.phone || '').localeCompare(b.phone || ''),
        },
    ];

    const handleDeleteLocation = async (location: Location) => {
        if (locations.length === 1) {
            notification.error({
                message: t('notification.error'),
                description: t('location.notification.deleteErrorLast'),
                placement: 'bottomRight',
            });
            return;
        }
        setIsDeleting(true);
        try {
            // Fetch all kiosks
            const kiosks = await getRequest(`/orgs/${orgId}/kiosk`);
            const filteredKiosks = kiosks.data.filter(
                (kiosk: Kiosk) =>
                    kiosk.site.id === location.id
            );

            // Check if there are any connected kiosks
            if (filteredKiosks.length > 0) {
                notification.error({
                    message: t('notification.error'),
                    description: t('location.notification.deleteErrorKiosk'),
                    placement: 'bottomRight',
                });
                return;
            }

            await deleteRequest(`/orgs/${orgId}/sites/${location.id}`);
            await postRequest(`/orgs/${orgId}/userActivity`, {
                userId: globalUserId,
                activityId: LOCATION_DELETED_ID,
                details: location.name
            });

            // Re-fetch the updated list of locations
            const locationsResult = await dispatch(fetchLocations({ orgId, status: 1 })).unwrap();

            if (globalLocationId === -1 && locationsResult.data.length === 1) {
                // Only one location left, -> set it as current
                const onlyLocation = locationsResult.data[0];
                await dispatch(describeLocation({ orgId, locationId: onlyLocation.id, global: true }));
                await dispatch(describeLocationWorkflow({ orgId, locationId: onlyLocation.id, global: true }));
            } else if (location.id === globalLocationId && locationsResult.data.length > 0) {
                // Deleted selected location -> fallback to either default location or first location
                let fallbackLocation;

                // Try matching with user's default location
                if (selectedUserSites?.length > 0) {
                    const defaultLocation = locationsResult.data.find(location => location.id === selectedUserSites[0].id);
                    if (defaultLocation) {
                        fallbackLocation = defaultLocation;
                    }
                }
                // Otherwise, pick a first location
                if (!fallbackLocation) {
                    fallbackLocation = locationsResult.data[0];
                }

                await dispatch(describeLocation({ orgId, locationId: fallbackLocation.id, global: true }));
                await dispatch(describeLocationWorkflow({ orgId, locationId: fallbackLocation.id, global: true }));
            }

            notification.success({
                message: t('notification.success'),
                description: t('location.notification.deleteSuccess'),
                placement: 'bottomRight',
            });

        } catch (error: any) {
            const isUserAssignedConflict =
                error?.response?.status === 409 &&
                typeof error?.response?.data?.message === 'string' &&
                error.response.data.message.toLowerCase().includes('users assigned');

            notification.error({
                message: t('notification.error'),
                description: isUserAssignedConflict
                    ? t('location.notification.deleteErrorUserAssigned')
                    : t('location.notification.deleteError'),
                placement: 'bottomRight',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleAddLocation = () => {
        handleAddClick();
    }

    const opTableProps: IOpTableProps = {
        dataSource: locations,
        columns: columns,
        rowActions: {
            onEditClick: (location: Location) => handleEditClick(location),
            onDeleteClick: hasLocationWrite ? handleDeleteLocation : undefined,
            deleteModalTitle: () => t('confirmDelete.title', { title: t('location.itemName') }),
            deleteModalContent: ({ record }) => t('confirmDelete.message', { name: record.name }),
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        writeAccess: hasLocationWrite,
        allowAddition: hasLocationWrite ? {
            itemName: t('location.itemName'),
            onClick: handleAddLocation,
        } : false,
        loading: fetchLocationsLoading || isDeleting,
        allowExport: true,
        allowShowHideColumns: true,
    };

    return <>
        <OpTable {...opTableProps} />
    </>;
};

export default LocationsTable;
